import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import PageHeader from '@components/common/PageHeader';
import FormSection from '@components/Form/FormSection/FormSection';
import Input from '@components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';
import Toggle from '@components/common/Toggle';

import coursesService from '@/services/courses/coursesService';
import cohortsService from '@/services/cohorts/cohortsService';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';
import SidebarModulesSelector from '@/components/Cohorts/SidebarModulesSelector';

interface DayFormValues {
    day_number: number;
    title: string;
    description: string;
    link: string;
    recorded_video: string;
    recorded_video_path: string;
    recorded_video_name: string;
    recordings: { path: string; name: string }[];
    class_time: string;
    attachments: string[];
    is_active: boolean;
    is_mentor_session?: boolean;
}

interface WeekFormValues {
    week_number: number;
    title?: string;
    is_active: boolean;
    days: DayFormValues[];
}

interface CohortCreateFormValues {
    course_id: string;
    cohort_name: string;
    description: string;
    start_date: string;
    registration_start: string;
    registration_end: string;
    duration: number;
    status: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    handbook_path?: string;
    module_template_ids?: string[];
    student_portal_version: 'CLASSIC' | 'JOURNEY';
    weeks: WeekFormValues[];
}

const CohortCreatePage = () => {
    const { t } = useTranslation('cohort-create');
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [openSections, setOpenSections] = useState<string[]>(['basic', 'dates', 'modules', 'schedule']);
    const [activeWeekTab, setActiveWeekTab] = useState<number>(1);
    const [openDays, setOpenDays] = useState<Record<number, boolean>>({ 1: true });
    const [uploadingVideos, setUploadingVideos] = useState<Record<string, boolean>>({});

    const toggleSection = (name: string) =>
        setOpenSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name],
        );

    // Initial 4 weeks * 7 days structure
    const initialWeeks = useMemo<WeekFormValues[]>(() => {
        return Array.from({ length: 4 }, (_, wIdx) => ({
            week_number: wIdx + 1,
            title: '',
            is_active: true,
            days: Array.from({ length: 7 }, (_, dIdx) => ({
                day_number: dIdx + 1,
                title: '',
                description: '',
                link: '',
                recorded_video: '',
                recorded_video_path: '',
                recorded_video_name: '',
                recordings: [],
                class_time: '',
                attachments: [],
                is_active: true,
            })),
        }));
    }, []);

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        trigger,
        formState: { errors, isSubmitting },
    } = useForm<CohortCreateFormValues>({
        mode: 'onSubmit',
        defaultValues: {
            course_id: '',
            cohort_name: '',
            description: '',
            start_date: '',
            registration_start: '',
            registration_end: '',
            duration: 30,
            status: 'ACTIVE',
            handbook_path: '',
            module_template_ids: [],
            student_portal_version: 'CLASSIC',
            weeks: initialWeeks,
        },
    });

    // Fetch active programs
    const { data: coursesResponse, isLoading: isLoadingCourses } = useQuery({
        queryKey: ['courses-lookup'],
        queryFn: () => coursesService.getCourses(1, 100, 'ACTIVE'),
    });

    const courses = useMemo(() => coursesResponse?.data?.data ?? [], [coursesResponse]);

    const courseOptions = useMemo(() => {
        return courses.map(c => ({
            value: c.id,
            label: c.course_name,
        }));
    }, [courses]);

    // Select AI Fellowship Program by default
    useEffect(() => {
        if (courses.length > 0) {
            const defaultCourse = courses.find(c =>
                c.course_name.toLowerCase().includes('fellowship') ||
                c.course_name.toLowerCase().includes('ai')
            ) ?? courses[0];
            if (defaultCourse && !getValues('course_id')) {
                setValue('course_id', defaultCourse.id);
            }
        }
    }, [courses, setValue, getValues]);

    // Live generated title preview
    const cohortName = useWatch({ control, name: 'cohort_name' });
    const startDate = useWatch({ control, name: 'start_date' });
    const registrationStart = useWatch({ control, name: 'registration_start' });
    const registrationEnd = useWatch({ control, name: 'registration_end' });

    const todayStr = useMemo(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const minRegEnd = useMemo(() => {
        return registrationStart || todayStr;
    }, [registrationStart, todayStr]);

    const minStartDate = useMemo(() => {
        if (!registrationEnd) return todayStr;
        try {
            const d = new Date(registrationEnd);
            d.setDate(d.getDate() + 1);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return todayStr;
        }
    }, [registrationEnd, todayStr]);

    // Clearing values when previous dates are adjusted to be invalid
    useEffect(() => {
        if (registrationStart && registrationEnd && registrationStart > registrationEnd) {
            setValue('registration_end', '');
            setValue('start_date', '');
        }
    }, [registrationStart, registrationEnd, setValue]);

    useEffect(() => {
        if (registrationEnd && startDate && registrationEnd >= startDate) {
            setValue('start_date', '');
        }
    }, [registrationEnd, startDate, setValue]);

    const generatedTitlePreview = useMemo(() => {
        if (!cohortName) return '';
        let trimmed = cohortName.trim();
        trimmed = trimmed.replace(/^(.+?)\s*\(\1\)$/i, '$1').trim();
        const match = trimmed.match(/^(.+?)\s*\((.+?)\)$/i);
        if (match && match[1].trim().toLowerCase() === match[2].trim().toLowerCase()) {
            trimmed = match[1].trim();
        }

        if (!startDate) return trimmed;
        try {
            const date = new Date(startDate);
            if (isNaN(date.getTime())) return trimmed;
            const month = date.toLocaleString('en-US', { month: 'long' });
            const year = date.getFullYear();
            const dateTag = `${month} ${year}`;

            const lowerNorm = trimmed.toLowerCase();
            const lowerTag = dateTag.toLowerCase();

            // Avoid repeating month/year if already present in cohort title
            if (lowerNorm === lowerTag || lowerNorm.endsWith(`(${lowerTag})`) || lowerNorm.includes(lowerTag)) {
                return trimmed;
            }

            return `${trimmed} (${dateTag})`;
        } catch {
            return trimmed;
        }
    }, [cohortName, startDate]);

    // Calculate dates next to days dynamically based on Cohort Start Date
    const getDayDateLabel = (weekNum: number, dayNum: number) => {
        if (!startDate) return '';
        try {
            const date = new Date(startDate);
            if (isNaN(date.getTime())) return '';
            const offset = (weekNum - 1) * 7 + (dayNum - 1);
            date.setDate(date.getDate() + offset);
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return '';
        }
    };

    const toggleDayAccordion = (dayNum: number) => {
        setOpenDays(prev => ({
            ...prev,
            [dayNum]: !prev[dayNum],
        }));
    };

    const addAttachment = (weekIdx: number, dayIdx: number) => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '*';
        fileInput.onchange = async (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            const uploadToast = toast.loading(`Uploading "${file.name}"...`);
            try {
                const res = await cohortsService.uploadFile(file);
                if (res.data?.filePath) {
                    const fileObj = JSON.stringify({
                        filePath: res.data.filePath,
                        fileName: res.data.fileName || file.name,
                        fileSize: res.data.fileSize || 'N/A',
                    });
                    const currentAttachments = getValues(`weeks.${weekIdx}.days.${dayIdx}.attachments`) || [];
                    setValue(`weeks.${weekIdx}.days.${dayIdx}.attachments`, [...currentAttachments, fileObj]);
                    void trigger(`weeks.${weekIdx}.days.${dayIdx}.attachments`);
                    toast.success('File uploaded and attached successfully!', { id: uploadToast });
                } else {
                    toast.error('Upload failed: invalid server response', { id: uploadToast });
                }
            } catch (err: any) {
                const errMsg = err.response?.data?.message || err.message || 'File upload failed';
                toast.error(`Upload failed: ${errMsg}`, { id: uploadToast });
            }
        };
        fileInput.click();
    };

    const removeAttachment = (weekIdx: number, dayIdx: number, attachmentIdx: number) => {
        const currentAttachments = getValues(`weeks.${weekIdx}.days.${dayIdx}.attachments`) || [];
        setValue(
            `weeks.${weekIdx}.days.${dayIdx}.attachments`,
            currentAttachments.filter((_, idx) => idx !== attachmentIdx),
        );
        void trigger(`weeks.${weekIdx}.days.${dayIdx}.attachments`);
    };

    const createCohortMutation = useMutation({
        mutationFn: (payload: CohortCreateFormValues) => cohortsService.createCohort(payload),
        onSuccess: (res) => {
            toast.success(res.message ?? t('toast.create_success'));
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
            navigate('/admin/cohorts', { replace: true });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, t('toast.create_error')));
        },
    });

    const onSubmit = (data: CohortCreateFormValues) => {
        const today = new Date();
        const formatDate = (d: Date) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        const regStart = data.registration_start || formatDate(today);
        const regEnd = data.registration_end || formatDate(new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000));
        const startDate = data.start_date || formatDate(new Date(today.getTime() + 8 * 24 * 60 * 60 * 1000));
        const courseId = data.course_id || courseOptions[0]?.value || '';

        const sanitizedData: CohortCreateFormValues = {
            ...data,
            course_id: courseId,
            description: data.description?.trim() || `${data.cohort_name} Program`,
            registration_start: regStart,
            registration_end: regEnd,
            start_date: startDate,
            weeks: (data.weeks || initialWeeks).map((w, wIdx) => ({
                ...w,
                days: (w.days || initialWeeks[wIdx]?.days || []).map((d) => ({
                    ...d,
                    title: d.title?.trim() || `Day ${d.day_number} Class`,
                    link: d.is_mentor_session
                        ? 'https://placeholder-mentor-link'
                        : (d.link?.trim() || ''),
                })),
            })),
        };
        createCohortMutation.mutate(sanitizedData);
    };

    // Live validation checks for submit button gating - allow when cohort title is provided
    const watchedAll = useWatch({ control });
    const isFormValid = useMemo(() => {
        return Boolean(watchedAll.cohort_name && watchedAll.cohort_name.trim());
    }, [watchedAll.cohort_name]);

    const activeWeekIndex = activeWeekTab - 1;

    return (
        <>
            <PageHeader
                title={t('page_title')}
                breadcrumbItems={[
                    { label: t('breadcrumb').split(' / ')[0]!.trim(), to: '/admin/dashboard' },
                    { label: t('breadcrumb').split(' / ')[1]!.trim(), to: '/admin/cohorts' },
                    { label: t('breadcrumbspan').trim() },
                ]}
            />
            <form
                id="cohort-create-form"
                className="flex flex-col px-4 pb-24"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                {/* 1. Basic Info Section */}
                <FormSection
                    title={t('sections.basic_info')}
                    icon={<Icon name="formSection" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                    isOpen={openSections.includes('basic')}
                    onToggle={() => toggleSection('basic')}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="course_id"
                            control={control}
                            rules={{ required: t('validation.course_required') }}
                            render={({ field }) => (
                                <Select
                                    label={t('fields.course_label')}
                                    placeholder="Select course"
                                    options={courseOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.course_id?.message}
                                    isDisabled={isLoadingCourses}
                                    required
                                />
                            )}
                        />

                        <Controller
                            name="cohort_name"
                            control={control}
                            rules={{ required: t('validation.name_required') }}
                            render={({ field }) => (
                                <Input
                                    label={t('fields.cohort_name_label')}
                                    placeholder={t('fields.cohort_name_placeholder')}
                                    error={errors.cohort_name?.message}
                                    required
                                    {...field}
                                />
                            )}
                        />

                        <div className="col-span-1 md:col-span-2">
                            <Input
                                label={t('fields.generated_title_label')}
                                placeholder={t('fields.generated_title_placeholder')}
                                value={generatedTitlePreview}
                                disabled
                                className="bg-gray-50 border-gray-200 text-gray-500 font-semibold"
                            />
                        </div>

                        <div className="col-span-1 md:col-span-2">
                            <Controller
                                name="description"
                                control={control}
                                rules={{ required: t('validation.description_required') }}
                                render={({ field }) => (
                                    <Textarea
                                        label={t('fields.description_label')}
                                        placeholder={t('fields.description_placeholder')}
                                        rows={4}
                                        error={errors.description?.message}
                                        required
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-span-1 md:col-span-2">
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-gray-200 bg-[var(--primary-canvas-color)]">
                                        <span className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)]">
                                            {t('fields.status_label')}
                                        </span>
                                        <Toggle
                                            active={field.value === 'ACTIVE'}
                                            onChange={(active) => field.onChange(active ? 'ACTIVE' : 'INACTIVE')}
                                        />
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-1 md:col-span-2">
                            <label className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] block mb-1">
                                Student Handbook (Optional)
                            </label>
                            <Controller
                                name="handbook_path"
                                control={control}
                                render={({ field }) => {
                                    const [uploading, setUploading] = useState(false);
                                    
                                    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        
                                        const toastId = toast.loading(`Uploading "${file.name}"...`);
                                        try {
                                            setUploading(true);
                                            const res = await cohortsService.uploadFile(file);
                                            if (res.data?.filePath) {
                                                field.onChange(res.data.filePath);
                                                toast.success('Handbook uploaded successfully!', { id: toastId });
                                            } else {
                                                toast.error('Upload failed', { id: toastId });
                                            }
                                        } catch (err: any) {
                                            toast.error(err.response?.data?.message || err.message || 'Upload failed', { id: toastId });
                                        } finally {
                                            setUploading(false);
                                        }
                                    };

                                    return (
                                        <div className="flex flex-col gap-2 p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                            {field.value ? (
                                                <div className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs">
                                                    <span className="text-xs font-medium text-gray-700 truncate max-w-md">
                                                        {typeof field.value === 'string' ? field.value.split('/').pop() : ''}
                                                    </span>
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="text-red-500 hover:text-red-700 text-xs py-0 px-2 cursor-pointer"
                                                        onClick={() => field.onChange('')}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.doc,.docx"
                                                        id="handbook-upload"
                                                        className="hidden"
                                                        onChange={handleUpload}
                                                        disabled={uploading}
                                                    />
                                                    <label
                                                        htmlFor="handbook-upload"
                                                        className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-xs cursor-pointer ${
                                                            uploading ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        Upload Handbook
                                                    </label>
                                                    <span className="text-xs text-gray-500">
                                                        PDF, DOC, DOCX up to 10MB
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>
                </FormSection>

                {/* 2. Dates Section */}
                <FormSection
                    title={t('sections.dates_info')}
                    icon={<Icon name="schdulecalender" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                    isOpen={openSections.includes('dates')}
                    onToggle={() => toggleSection('dates')}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                         <Controller
                            name="registration_start"
                            control={control}
                            rules={{ required: t('validation.reg_start_required') }}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    label={t('fields.registration_start_label')}
                                    error={errors.registration_start?.message}
                                    required
                                    {...field}
                                />
                            )}
                        />

                        <Controller
                            name="registration_end"
                            control={control}
                            rules={{ required: t('validation.reg_end_required') }}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    label={t('fields.registration_end_label')}
                                    error={errors.registration_end?.message}
                                    required
                                    {...field}
                                    min={minRegEnd}
                                />
                            )}
                        />

                        <Controller
                            name="start_date"
                            control={control}
                            rules={{ required: t('validation.start_date_required') }}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    label={t('fields.start_date_label')}
                                    error={errors.start_date?.message}
                                    required
                                    {...field}
                                    min={minStartDate}
                                />
                            )}
                        />

                        <Controller
                            name="duration"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    label={t('fields.duration_label')}
                                    disabled
                                    {...field}
                                    className="bg-gray-100 cursor-not-allowed opacity-75"
                                />
                            )}
                        />
                    </div>
                </FormSection>

                {/* 3. Student Sidebar Modules Configuration */}
                <FormSection
                    title="Student Sidebar Modules"
                    icon={<Icon name="sidebarSection" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                    isOpen={openSections.includes('modules')}
                    onToggle={() => toggleSection('modules')}
                >
                    <div className="flex flex-col gap-2">
                        <p className="text-xs text-gray-500 mb-2">
                            Select which navigation modules should appear in the student sidebar for this cohort.
                        </p>
                        <Controller
                            name="module_template_ids"
                            control={control}
                            render={({ field }) => (
                                <SidebarModulesSelector
                                    value={field.value}
                                    onChange={field.onChange}
                                    autoSelectDefaults={true}
                                />
                            )}
                        />

                        <div className="mt-4">
                            <Controller
                                name="student_portal_version"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Student Portal Experience"
                                        options={[
                                            { value: 'CLASSIC', label: 'Classic (Cohort 1 layout)' },
                                            { value: 'JOURNEY', label: 'Student Journey (Cohort 2+)' },
                                        ]}
                                        value={field.value}
                                        onChange={(v) => field.onChange(v || 'CLASSIC')}
                                        isClearable={false}
                                    />
                                )}
                            />
                            <p className="text-form-helper text-gray-400 mt-1">
                                Leave on <b>Classic</b> unless this cohort should use the new Journey portal.
                            </p>
                        </div>
                    </div>
                </FormSection>

                {/* 4. Schedule Designer Section */}
                <FormSection
                    title={t('sections.schedule')}
                    icon={<Icon name="auditquestiondesign" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                    isOpen={openSections.includes('schedule')}
                    onToggle={() => toggleSection('schedule')}
                >
                    {/* Week Navigation Tabs */}
                    <div className="flex border-b border-gray-200 mb-6 bg-gray-50 p-1.5 rounded-xl gap-2">
                        {[1, 2, 3, 4].map(wNum => (
                            <button
                                key={wNum}
                                type="button"
                                onClick={() => setActiveWeekTab(wNum)}
                                className={`flex-1 py-2.5 text-center text-sm font-semibold rounded-lg transition-all duration-150 cursor-pointer ${
                                    activeWeekTab === wNum
                                        ? 'bg-white text-[var(--primary-color)] shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Week {wNum}
                            </button>
                        ))}
                    </div>

                    {/* Week visibility toggle */}
                    <Controller
                        name={`weeks.${activeWeekIndex}.is_active`}
                        control={control}
                        render={({ field }) => (
                            <div className="flex items-center justify-between gap-2 px-4 py-3 mb-6 rounded-lg border border-gray-200 bg-[var(--primary-canvas-color)]">
                                <div className="flex flex-col">
                                    <span className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)]">
                                        Week {activeWeekTab} Visibility
                                    </span>
                                    <span className="text-[11px] text-gray-500">
                                        {field.value ? 'Visible to students in the portal' : 'Hidden from students in the portal'}
                                    </span>
                                </div>
                                <Toggle
                                    active={!!field.value}
                                    onChange={(active) => field.onChange(active)}
                                />
                            </div>
                        )}
                    />

                    {/* Week Title Field */}
                    <div className="mb-6 bg-white p-4 border border-gray-200 rounded-xl shadow-xs">
                        <Controller
                            name={`weeks.${activeWeekIndex}.title`}
                            control={control}
                            render={({ field }) => (
                                <Input
                                    label={`Week ${activeWeekTab} Title`}
                                    placeholder="Enter week title (e.g. Intro to Python, Capstone Project)"
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    error={errors.weeks?.[activeWeekIndex]?.title?.message}
                                />
                            )}
                        />
                    </div>

                    {/* Day Schedule List */}
                    <div className="flex flex-col gap-4">
                        {initialWeeks[activeWeekIndex]?.days.map((_, dayIdx) => {
                            const dayNum = dayIdx + 1;
                            const isMentorSession = watchedAll.weeks?.[activeWeekIndex]?.days?.[dayIdx]?.is_mentor_session;
                            const isDayOpen = !!openDays[dayNum];
                            const dateLabel = getDayDateLabel(activeWeekTab, dayNum);

                            return (
                                <div
                                    key={`week-${activeWeekTab}-day-${dayNum}`}
                                    className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:border-gray-300 transition-colors"
                                >
                                    {/* Collapsible Header */}
                                    <div
                                        onClick={() => toggleDayAccordion(dayNum)}
                                        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--primary-surface-color)] text-[var(--primary-color)] text-sm font-bold shadow-inner">
                                                D{dayNum}
                                            </span>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-[var(--secondary-color)]">
                                                    Day {dayNum} Class
                                                </span>
                                                {dateLabel && (
                                                    <span className="text-[11px] text-gray-500 font-medium">
                                                        {dateLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Controller
                                                name={`weeks.${activeWeekIndex}.days.${dayIdx}.is_active`}
                                                control={control}
                                                render={({ field }) => (
                                                    <span
                                                        className="flex items-center gap-1.5"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={field.value ? 'Visible to students' : 'Hidden from students'}
                                                    >
                                                        <span className="text-[10px] font-semibold text-gray-400 hidden sm:inline">
                                                            {field.value ? 'Visible' : 'Hidden'}
                                                        </span>
                                                        <Toggle
                                                            active={!!field.value}
                                                            onChange={(active) => field.onChange(active)}
                                                        />
                                                    </span>
                                                )}
                                            />
                                            <span className={`transition-transform duration-200 ${isDayOpen ? 'rotate-180' : ''}`}>
                                                <Icon name="downarrow" className="w-[10px] h-auto" />
                                            </span>
                                        </div>
                                    </div>

                                    {/* Collapsible Body */}
                                    {isDayOpen && (
                                        <div className="p-4 bg-white grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100">
                                            <Controller
                                                name={`weeks.${activeWeekIndex}.days.${dayIdx}.title`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        label={t('fields.day_title_label')}
                                                        placeholder={t('fields.day_title_placeholder')}
                                                        error={errors.weeks?.[activeWeekIndex]?.days?.[dayIdx]?.title?.message}
                                                        {...field}
                                                    />
                                                )}
                                            />

                                            {isMentorSession ? (
                                                <div className="p-3 bg-indigo-50/50 border border-indigo-100/50 rounded-xl text-xs font-semibold text-indigo-700 flex flex-col gap-1">
                                                    <span className="font-extrabold uppercase text-[9px] tracking-wider text-indigo-500">Mentor Session Mode</span>
                                                    <span>Student-specific links will be configured post-creation in the Cohort Details page.</span>
                                                </div>
                                            ) : (
                                                <Controller
                                                    name={`weeks.${activeWeekIndex}.days.${dayIdx}.link`}
                                                    control={control}
                                                    rules={{
                                                        validate: (val) =>
                                                            !val ||
                                                            /^https?:\/\/.+/.test(val) ||
                                                            t('validation.day_link_invalid'),
                                                    }}
                                                    render={({ field }) => (
                                                        <Input
                                                            label={t('fields.day_link_label')}
                                                            placeholder={t('fields.day_link_placeholder')}
                                                            error={errors.weeks?.[activeWeekIndex]?.days?.[dayIdx]?.link?.message}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            )}

                                            {!isMentorSession && (
                                                 <Controller
                                                     name={`weeks.${activeWeekIndex}.days.${dayIdx}.class_time`}
                                                     control={control}
                                                     render={({ field }) => (
                                                         <Input
                                                             type="time"
                                                             label="Class Time"
                                                             error={errors.weeks?.[activeWeekIndex]?.days?.[dayIdx]?.class_time?.message}
                                                             {...field}
                                                         />
                                                     )}
                                                 />
                                             )}

                                             <Controller
                                                 name={`weeks.${activeWeekIndex}.days.${dayIdx}.recorded_video`}
                                                 control={control}
                                                 render={({ field }) => (
                                                     <Input
                                                         label={t('fields.recorded_video_label')}
                                                         placeholder={t('fields.recorded_video_placeholder')}
                                                         {...field}
                                                     />
                                                 )}
                                             />

                                              <Controller
                                                  name={`weeks.${activeWeekIndex}.days.${dayIdx}.is_mentor_session`}
                                                  control={control}
                                                  render={({ field }) => (
                                                      <div className="flex items-center gap-2 px-1 py-2">
                                                          <input
                                                              type="checkbox"
                                                              id={`weeks.${activeWeekIndex}.days.${dayIdx}.is_mentor_session`}
                                                              checked={!!field.value}
                                                              onChange={(e) => field.onChange(e.target.checked)}
                                                              className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                                                          />
                                                          <label
                                                              htmlFor={`weeks.${activeWeekIndex}.days.${dayIdx}.is_mentor_session`}
                                                              className="text-xs font-bold text-[var(--secondary-color)] cursor-pointer select-none"
                                                          >
                                                              Mentor Session
                                                          </label>
                                                      </div>
                                                  )}
                                              />

                                             <div className="col-span-1 md:col-span-2 flex flex-col gap-4 p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                                 <div className="flex items-center justify-between">
                                                     <span className="text-xs font-semibold text-[var(--secondary-color)]">
                                                         Class Recordings (Multiple Uploads Allowed)
                                                     </span>
                                                     <span className="text-[10px] text-gray-400">
                                                         MP4, MOV, AVI, MKV formats (Bypasses server memory)
                                                     </span>
                                                 </div>

                                                 <Controller
                                                     name={`weeks.${activeWeekIndex}.days.${dayIdx}.recordings`}
                                                     control={control}
                                                     render={({ field: recordingsField }) => {
                                                         const recordingsList = recordingsField.value || [];

                                                         const [localVideoName, setLocalVideoName] = useState('');
                                                         const key = `${activeWeekIndex}-${dayIdx}`;
                                                         const isUploading = !!uploadingVideos[key];

                                                         const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                             const file = e.target.files?.[0];
                                                             if (!file) return;

                                                             const toastId = toast.loading(`Uploading video "${file.name}"...`);
                                                             try {
                                                                 setUploadingVideos(prev => ({ ...prev, [key]: true }));
                                                                 const res = await cohortsService.uploadVideo(file, localVideoName || undefined);
                                                                 if (res.data?.filePath) {
                                                                     const newRecording = {
                                                                         path: res.data.filePath,
                                                                         name: localVideoName || res.data.fileName || file.name,
                                                                     };
                                                                     recordingsField.onChange([...recordingsList, newRecording]);
                                                                     setLocalVideoName('');
                                                                     toast.success('Video uploaded successfully!', { id: toastId });
                                                                 } else {
                                                                     toast.error('Upload failed', { id: toastId });
                                                                 }
                                                             } catch (err: any) {
                                                                 toast.error(err.response?.data?.message || err.message || 'Upload failed', { id: toastId });
                                                             } finally {
                                                                 setUploadingVideos(prev => ({ ...prev, [key]: false }));
                                                             }
                                                         };

                                                         const removeRecording = (indexToRemove: number) => {
                                                             const updated = recordingsList.filter((_, idx) => idx !== indexToRemove);
                                                             recordingsField.onChange(updated);
                                                             toast.success('Recording removed');
                                                         };

                                                         return (
                                                             <div className="flex flex-col gap-3">
                                                                 {/* List of uploaded recordings */}
                                                                 {recordingsList.length > 0 && (
                                                                     <div className="flex flex-col gap-2 max-h-40 overflow-y-auto bg-white p-2 rounded-lg border border-gray-100">
                                                                         {recordingsList.map((rec: any, recIdx: number) => (
                                                                             <div 
                                                                                 key={recIdx} 
                                                                                 className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-md border border-gray-100 shadow-2xs transition-colors"
                                                                             >
                                                                                 <div className="flex flex-col min-w-0 pr-2">
                                                                                     <span className="text-xs font-semibold text-gray-800 truncate">
                                                                                         {rec.name}
                                                                                     </span>
                                                                                     <span className="text-[10px] text-gray-400 truncate">
                                                                                         {rec.path.split('/').pop()}
                                                                                     </span>
                                                                                 </div>
                                                                                 <Button
                                                                                     type="button"
                                                                                     variant="link"
                                                                                     className="text-red-500 hover:text-red-700 text-xs py-0 px-2 cursor-pointer"
                                                                                     onClick={() => removeRecording(recIdx)}
                                                                                 >
                                                                                     Remove
                                                                                 </Button>
                                                                             </div>
                                                                         ))}
                                                                     </div>
                                                                 )}

                                                                 {/* Add new recording form */}
                                                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end bg-white/40 p-3 rounded-lg border border-gray-200/60">
                                                                     <div>
                                                                         <Input
                                                                             label="Rename Video File (Optional)"
                                                                             placeholder="Enter display name (e.g. Session 1 - Intro)"
                                                                             value={localVideoName}
                                                                             onChange={(e) => setLocalVideoName(e.target.value)}
                                                                         />
                                                                     </div>

                                                                     <div className="flex items-center gap-3">
                                                                         <input
                                                                             type="file"
                                                                             accept=".mp4,.mov,.avi,.mkv"
                                                                             disabled={isUploading}
                                                                             onChange={handleVideoUpload}
                                                                             className="hidden"
                                                                             id={`video-upload-input-${key}`}
                                                                         />
                                                                         <label
                                                                             htmlFor={`video-upload-input-${key}`}
                                                                             className={`w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-xs cursor-pointer transition-all duration-150 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                         >
                                                                             {isUploading ? 'Uploading...' : 'Select & Upload Video'}
                                                                         </label>
                                                                     </div>
                                                                 </div>
                                                             </div>
                                                         );
                                                     }}
                                                 />
                                             </div>

                                            <div className="col-span-1 md:col-span-2">
                                                <Controller
                                                    name={`weeks.${activeWeekIndex}.days.${dayIdx}.description`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Textarea
                                                            label={t('fields.day_description_label')}
                                                            placeholder={t('fields.day_description_placeholder')}
                                                            rows={2}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <Controller
                                                name={`weeks.${activeWeekIndex}.days.${dayIdx}.recorded_video`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        label={t('fields.recorded_video_label')}
                                                        placeholder={t('fields.recorded_video_placeholder')}
                                                        {...field}
                                                    />
                                                )}
                                            />

                                            {/* Resource Attachments Upload List */}
                                            <div className="col-span-1 md:col-span-2 border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50/50">
                                                <div className="flex items-center justify-between mb-3">
                                                    <label className="text-xs font-semibold text-[var(--secondary-color)]">
                                                        {t('fields.attachments_label')}
                                                    </label>
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        onClick={() => addAttachment(activeWeekIndex, dayIdx)}
                                                        className="text-xs py-1"
                                                    >
                                                        <Icon name="plus" className="w-3 h-auto mr-1" />
                                                        {t('buttons.add_attachment')}
                                                    </Button>
                                                </div>

                                                <Controller
                                                    name={`weeks.${activeWeekIndex}.days.${dayIdx}.attachments`}
                                                    control={control}
                                                    render={({ field }) => {
                                                        const items = field.value || [];
                                                        if (items.length === 0) {
                                                            return (
                                                                <span className="text-xs text-gray-400 italic block py-1.5">
                                                                    No resource attachments added for this day.
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <div className="flex flex-col gap-1.5">
                                                                {items.map((file, fIdx) => {
                                                                    let parsed = { filePath: file, fileName: file, fileSize: 'N/A' };
                                                                    try {
                                                                        if (file.startsWith('{') && file.endsWith('}')) {
                                                                            parsed = JSON.parse(file);
                                                                        } else {
                                                                            parsed.fileName = file.split('/').pop()?.replace(/^\d+-/, '') || file;
                                                                        }
                                                                    } catch {}

                                                                    const fileUrl = parsed.filePath.startsWith('http') 
                                                                        ? parsed.filePath 
                                                                        : `${import.meta.env.VITE_API_BASE_URL ? import.meta.env.VITE_API_BASE_URL.replace(/\/api\/v1\/?$/, '') : ''}${parsed.filePath}`;

                                                                    return (
                                                                        <div
                                                                            key={fIdx}
                                                                            className="flex items-center justify-between px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs"
                                                                        >
                                                                            <a
                                                                                href={fileUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-semibold text-[var(--primary-color)] hover:underline truncate max-w-[80%]"
                                                                            >
                                                                                {parsed.fileName} <span className="text-[10px] text-gray-400 font-normal">({parsed.fileSize})</span>
                                                                            </a>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeAttachment(activeWeekIndex, dayIdx, fIdx)}
                                                                                className="text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                                                            >
                                                                                <Icon name="close" className="w-3.5 h-auto" />
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </FormSection>

                {/* Footer submit button gating bar */}
                <div className="flex items-center justify-end gap-3 fixed bottom-0 right-0 left-[50px] xl:left-[220px] 2xl:left-[250px] w-[-webkit-fill-available] bg-white border-t border-gray-200 p-4 shadow-md z-10">
                    <Button
                        type="button"
                        variant="dark"
                        onClick={() => navigate('/admin/cohorts')}
                        disabled={isSubmitting}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        form="cohort-create-form"
                        variant="primary"
                        disabled={isSubmitting || !isFormValid}
                    >
                        {isSubmitting ? t('buttons.updating') : t('buttons.submit')}
                    </Button>
                </div>
            </form>
        </>
    );
};

export default CohortCreatePage;
